/**
 * 改良版統計情報パネル
 * マップの統計情報と設定を表示するパネル
 */

import React, { useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  BarChart2Icon, 
  CheckCircleIcon, 
  ClockIcon, 
  EyeIcon, 
  HashIcon, 
  LayersIcon, 
  LineChartIcon, 
  XCircleIcon,
  EyeOffIcon,
  PlayIcon,
  PauseIcon
} from 'lucide-react';
import { useMapUI, useMapState } from '@/contexts/map';
import { FilterVisibility } from '@/contexts/map/MapUIContext';

/**
 * ドーナツチャートコンポーネント
 */
const DonutChart = ({ 
  active, 
  inactive, 
  size = 120, 
  strokeWidth = 12,
  activeColor = 'var(--primary)',
  inactiveColor = 'var(--muted)'
}: { 
  active: number; 
  inactive: number; 
  size?: number;
  strokeWidth?: number;
  activeColor?: string;
  inactiveColor?: string;
}) => {
  const total = active + inactive;
  const activePercentage = total > 0 ? (active / total) * 100 : 0;
  const inactivePercentage = total > 0 ? (inactive / total) * 100 : 0;
  
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  const activeOffset = (100 - activePercentage) / 100 * circumference;
  const inactiveOffset = (100 - inactivePercentage) / 100 * circumference;
  
  const centerX = size / 2;
  const centerY = size / 2;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* アクティブ部分 */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={activeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={activeOffset}
          transform={`rotate(-90 ${centerX} ${centerY})`}
          strokeLinecap="round"
        />
        
        {/* 非アクティブ部分 */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={inactiveColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={inactiveOffset}
          transform={`rotate(${90 - activePercentage * 3.6} ${centerX} ${centerY})`}
          strokeLinecap="round"
          className="opacity-60"
        />
      </svg>
      
      <div 
        className="absolute inset-0 flex flex-col items-center justify-center text-center"
        style={{ padding: strokeWidth }}
      >
        <div className="text-xl font-bold">{Math.round(activePercentage)}%</div>
        <div className="text-xs text-muted-foreground">アクティブ</div>
      </div>
    </div>
  );
};

/**
 * 縦棒グラフコンポーネント
 */
const BarChart = ({ 
  data 
}: { 
  data: { label: string; value: number; color?: string }[] 
}) => {
  const maxValue = Math.max(...data.map(item => item.value), 1);
  
  return (
    <div className="flex h-32 items-end gap-1 pb-4 pt-2">
      {data.map((item, index) => {
        const percentage = (item.value / maxValue) * 100;
        
        return (
          <div 
            key={index} 
            className="flex flex-col items-center justify-end flex-1"
          >
            <div className="flex flex-col items-center">
              <span className="text-xs mb-1">{item.value || ''}</span>
              <div
                className="rounded-t-sm w-full"
                style={{
                  height: `${Math.max(percentage, 5)}%`,
                  backgroundColor: item.color || 'var(--primary)',
                  minHeight: '4px',
                  opacity: item.value > 0 ? 1 : 0.3
                }}
              />
            </div>
            <span className="text-xs mt-1 text-muted-foreground">{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * 統計情報パネルコンポーネント
 */
const StatsPanel: React.FC = () => {
  const { 
    filteredNodes, 
    filteredEdges, 
    filterVisibility, 
    setFilterVisibility,
    animateLayout,
    toggleAnimateLayout
  } = useMapUI();
  const { mapData } = useMapState();
  
  // アクティブ/非アクティブノード
  const activeNodes = React.useMemo(() => {
    return mapData?.nodes.filter(node => node.active) || [];
  }, [mapData]);
  
  const inactiveNodes = React.useMemo(() => {
    return mapData?.nodes.filter(node => !node.active) || [];
  }, [mapData]);
  
  // フェーズごとのノード数
  const nodesByPhase = React.useMemo(() => {
    const result = new Map<string, { active: number; inactive: number; color: string }>();
    
    if (mapData && mapData.phases) {
      mapData.phases.forEach(phase => {
        result.set(phase.id, { active: 0, inactive: 0, color: phase.color });
      });
    }
    
    if (mapData && mapData.nodes) {
      mapData.nodes.forEach(node => {
        if (node.phaseId) {
          const phaseData = result.get(node.phaseId);
          if (phaseData) {
            if (node.active) {
              phaseData.active += 1;
            } else {
              phaseData.inactive += 1;
            }
          }
        }
      });
    }
    
    return result;
  }, [mapData]);
  
  // 重要度ごとのノード数
  const nodesByImportance = React.useMemo(() => {
    const counts = [0, 0, 0, 0, 0]; // インデックス0は未使用

    if (mapData && mapData.nodes) {
      mapData.nodes.forEach(node => {
        if (node.active && node.importance >= 1 && node.importance <= 5) {
          counts[node.importance - 1] += 1;
        }
      });
    }
    
    return counts;
  }, [mapData]);
  
  // 表示フィルター変更ハンドラー
  const handleFilterVisibilityChange = useCallback((value: FilterVisibility) => {
    setFilterVisibility(value);
  }, [setFilterVisibility]);
  
  // mapDataがない場合はロード中表示
  if (!mapData || !mapData.phases) {
    return <div>Loading stats...</div>;
  }
  
  // フェーズごとのグラフデータを準備
  const phaseChartData = Array.from(nodesByPhase.entries())
    .filter(([_, data]) => data.active > 0 || data.inactive > 0)
    .map(([phaseId, data]) => {
      const phase = mapData.phases.find(p => p.id === phaseId);
      return {
        id: phaseId,
        name: phase?.name || phaseId,
        active: data.active,
        inactive: data.inactive,
        color: data.color
      };
    });
  
  // 重要度ごとのグラフデータを準備
  const importanceChartData = nodesByImportance.map((value, index) => ({
    label: `${index + 1}`,
    value,
    color: `hsl(${220 + index * 25}, 70%, ${55 + index * 5}%)`
  }));
  
  return (
    <Card className="shadow-none border-0 h-full flex flex-col">
      <CardHeader className="px-4 py-3 border-b">
        <CardTitle className="text-base flex items-center">
          <BarChart2Icon className="h-4 w-4 mr-2" />
          統計情報
        </CardTitle>
      </CardHeader>
      
      <Tabs defaultValue="overview" className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 border-b">
          <TabsList className="h-9 w-full grid grid-cols-3">
            <TabsTrigger value="overview">概要</TabsTrigger>
            <TabsTrigger value="phases">フェーズ</TabsTrigger>
            <TabsTrigger value="settings">設定</TabsTrigger>
          </TabsList>
        </div>
        
        <ScrollArea className="flex-1">
          <TabsContent value="overview" className="m-0 p-4">
            <div className="space-y-4">
              {/* アクティブ率ドーナツチャート */}
              <div className="flex flex-col items-center justify-center py-2">
                <DonutChart 
                  active={activeNodes.length} 
                  inactive={inactiveNodes.length}
                />
                <div className="flex justify-center mt-2 gap-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-primary" />
                    <span className="text-xs">アクティブ: {activeNodes.length}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-muted" />
                    <span className="text-xs">非アクティブ: {inactiveNodes.length}</span>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* 基本情報 */}
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <HashIcon className="h-4 w-4" />
                  基本情報
                </h3>
                <div className="bg-muted/30 rounded-md">
                  <div className="grid grid-cols-2 gap-0.5 p-0.5">
                    <div className="bg-card p-2 rounded-sm">
                      <div className="text-xs text-muted-foreground mb-1">表示トピック</div>
                      <div className="font-medium">{filteredNodes.length}</div>
                    </div>
                    <div className="bg-card p-2 rounded-sm">
                      <div className="text-xs text-muted-foreground mb-1">表示関連性</div>
                      <div className="font-medium">{filteredEdges.length}</div>
                    </div>
                    <div className="bg-card p-2 rounded-sm">
                      <div className="text-xs text-muted-foreground mb-1">トピック総数</div>
                      <div className="font-medium">{mapData.nodes.length}</div>
                    </div>
                    <div className="bg-card p-2 rounded-sm">
                      <div className="text-xs text-muted-foreground mb-1">会話文字数</div>
                      <div className="font-medium">{mapData?.transcript?.length || 0}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* 重要度分布 */}
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <LineChartIcon className="h-4 w-4" />
                  重要度分布
                </h3>
                <div className="bg-muted/30 p-3 rounded-md">
                  <BarChart data={importanceChartData} />
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="phases" className="m-0 p-4">
            <div className="space-y-4">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <LayersIcon className="h-4 w-4" />
                フェーズ別分布
              </h3>
              
              {phaseChartData.length === 0 ? (
                <div className="bg-muted/30 p-6 rounded-md flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">データがありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {phaseChartData.map(phase => (
                    <div key={phase.id} className="bg-muted/30 p-3 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: phase.color }}
                          />
                          <span className="text-sm font-medium">{phase.name}</span>
                        </div>
                        <Badge variant={phase.active > 0 ? "outline" : "secondary"} className="h-5 text-xs">
                          {phase.active} / {phase.active + phase.inactive}
                        </Badge>
                      </div>
                      
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        {phase.active > 0 && (
                          <div 
                            className="h-full rounded-full" 
                            style={{ 
                              width: `${(phase.active / (phase.active + phase.inactive)) * 100}%`,
                              backgroundColor: phase.color
                            }} 
                          />
                        )}
                      </div>
                      
                      <div className="flex justify-between mt-1.5 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <CheckCircleIcon className="h-3 w-3 text-green-500" />
                          <span>アクティブ: {phase.active}</span>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <XCircleIcon className="h-3 w-3 text-muted-foreground" />
                          <span>非アクティブ: {phase.inactive}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <Separator />
              
              {/* 時系列情報 */}
              <div>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <ClockIcon className="h-4 w-4" />
                  時系列情報
                </h3>
                <div className="bg-muted/30 p-4 rounded-md">
                  <div className="flex flex-col items-center justify-center text-center py-2">
                    <p className="text-sm text-muted-foreground">
                      時系列分析データはまだありません
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      より多くのデータを収集中です
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="settings" className="m-0 p-4">
            <div className="space-y-6">
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                <EyeIcon className="h-4 w-4" />
                表示設定
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-3 block">ノード表示フィルター</Label>
                  <RadioGroup 
                    value={filterVisibility}
                    onValueChange={(value) => handleFilterVisibilityChange(value as FilterVisibility)}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="all" id="filter-all" />
                      <Label htmlFor="filter-all" className="flex items-center gap-2">
                        <EyeIcon className="h-4 w-4" />
                        すべて表示
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="active-only" id="filter-active" />
                      <Label htmlFor="filter-active" className="flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4" />
                        アクティブのみ表示
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="inactive-only" id="filter-inactive" />
                      <Label htmlFor="filter-inactive" className="flex items-center gap-2">
                        <EyeOffIcon className="h-4 w-4" />
                        非アクティブのみ表示
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="animate-layout">レイアウトアニメーション</Label>
                    <p className="text-xs text-muted-foreground">
                      レイアウト変更時のアニメーションを有効にします
                    </p>
                  </div>
                  <Switch 
                    id="animate-layout" 
                    checked={animateLayout}
                    onCheckedChange={toggleAnimateLayout}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="animate-icon">
                      {animateLayout ? (
                        <div className="flex items-center gap-1">
                          <PlayIcon className="h-4 w-4 text-green-500" />
                          <span>アニメーションは有効です</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <PauseIcon className="h-4 w-4 text-amber-500" />
                          <span>アニメーションは無効です</span>
                        </div>
                      )}
                    </Label>
                  </div>
                </div>
                
                <Separator />
                
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="show-mini-map">ミニマップを表示</Label>
                    <p className="text-xs text-muted-foreground">
                      画面右下にマップの全体像を表示します
                    </p>
                  </div>
                  <Switch 
                    id="show-mini-map" 
                    checked={false}
                    disabled
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </Card>
  );
};

export default StatsPanel;